import { defineStore } from "pinia";
import { computed, ref } from "vue";
import AuthService from "../services/AuthService";
import { resolvePermissions, type PermissionCode } from "../config/permissions";
import type { Permission } from "../types/Permission";
import { getAuthoritiesFromAccessToken } from "../utils/jwt";

export const usePermissionStore = defineStore("permission", () => {
  const permissions = ref<Permission[]>([]);
  const hydrated = ref(false);

  const permissionSet = computed(
    () => new Set(permissions.value.map((permission) => permission.toUpperCase()))
  );

  function hydrateFromStorage() {
    const user = AuthService.getCurrentUser();
    if (!user || !AuthService.getToken()) {
      clear();
      return;
    }

    const tokenAuthorities = getAuthoritiesFromAccessToken(AuthService.getToken());
    permissions.value = resolvePermissions({
      isAdmin: user.isAdmin,
      authorities: tokenAuthorities,
      permissions: user.scopes,
    });
    hydrated.value = true;
  }

  function clear() {
    permissions.value = [];
    hydrated.value = false;
  }

  function hasPermission(permission?: Permission | null): boolean {
    if (!permission) return true;
    const value = String(permission);
    return permissions.value.includes(value) || permissionSet.value.has(value.toUpperCase());
  }

  function hasAnyPermission(required?: Permission[] | null): boolean {
    if (!required?.length) return true;
    return required.some((permission) => hasPermission(permission));
  }

  function hasAllPermissions(required?: Permission[] | null): boolean {
    if (!required?.length) return true;
    return required.every((permission) => hasPermission(permission));
  }

  return {
    permissions,
    hydrated,
    hydrateFromStorage,
    clear,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
});

export type { PermissionCode };
